/**
 * The Power and Energy preview's five steps (docs/foundations.md §8): a
 * solar roof. Models in web/src/lib/power.ts; the engine is
 * components/tracks/engine.tsx. Check plays the test out — the sun crosses
 * the sky, the months fill in, the night runs down the battery — before it
 * says what happened.
 */
import { useState, type ComponentType } from 'react';
import { useLocale } from '../../state/useLocale';
import { sound } from '../../lib/sound';
import {
  BATTERY_BUDGET,
  BATTERY_STOPS,
  BATTERY_USD_PER_KWH,
  BERLIN_USE,
  CABLE_A,
  CABLE_M,
  CAIRO_USE,
  CITIES,
  CITY_IDS,
  COVER_MAX,
  COVER_MIN,
  DROP_LIMIT,
  FACING,
  IRIS_PANELS,
  IRIS_TILT,
  MAINS_V,
  NIGHT_HOURS,
  NIGHT_KWH,
  POINTING_TARGET,
  ROOF_PANELS,
  SIZE_STOPS,
  TILT_STOPS,
  USABLE,
  battery,
  cable,
  checkTilt,
  monthlyOutput,
  sizeArray,
  type CityId,
  type Facing,
  type PowerStepId,
} from '../../lib/power';
import { Choice, IrisSays, Shell, Stops, at, idx, useLinear, useQ, useTest, useTested, type StepProps } from '../tracks/engine';
import { BRIEFS } from '../../lib/agent';

const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));

/* ── 1. Point the panel ────────────────────────────────────────────────── */

/** The compass, as eight buttons around a sky: N at the top. */
const COMPASS: (Facing | null)[] = ['NW', 'N', 'NE', 'W', null, 'E', 'SW', 'S', 'SE'];

function SkyFigure({ city, tilt, facing }: { city: CityId; tilt: number; facing: Facing }) {
  const compass = useLocale().strings.power.compass;
  const { tested, p } = useTested();
  const lat = CITIES[city].lat;
  // The sun's day seen from above: east to west, bowed toward the equator,
  // further the further the city is from it.
  const bow = (lat >= 0 ? 1 : -1) * (12 + Math.min(Math.abs(lat), 60) * 0.9);
  const cx = 100;
  const cy = 100;
  const sunX = cx + 70 - 140 * p;
  const sunY = cy + bow * Math.sin(Math.PI * p);
  // The panel as an arrow from the centre, the way it faces; a flat panel is a dot.
  const ang = (FACING[facing] * Math.PI) / 180; // from south, west positive
  const len = 62 * Math.sin((tilt * Math.PI) / 180);
  const px = cx - Math.sin(ang) * len;
  const py = cy + Math.cos(ang) * len;
  return (
    <svg className="ffig ffig--sky" viewBox="0 0 200 200" role="img" aria-hidden="true">
      <circle className="ffig__horizon" cx={cx} cy={cy} r={82} />
      <path className="ffig__sunpath" d={`M ${cx - 70} ${cy} Q ${cx} ${cy + 2 * bow} ${cx + 70} ${cy}`} fill="none" />
      <text className="ffig__tick" x={cx} y={12} textAnchor="middle">{compass.N}</text>
      <text className="ffig__tick" x={cx} y={196} textAnchor="middle">{compass.S}</text>
      <text className="ffig__tick" x={190} y={cy + 4} textAnchor="middle">{compass.E}</text>
      <text className="ffig__tick" x={10} y={cy + 4} textAnchor="middle">{compass.W}</text>
      <line className="ffig__panelarrow" x1={cx} y1={cy} x2={px} y2={py} />
      <rect className="ffig__panel" x={cx - 9} y={cy - 9} width={18} height={18} transform={`rotate(${-FACING[facing]} ${cx} ${cy})`} />
      {tested && <circle className="ffig__sun" cx={sunX} cy={sunY} r={8} />}
    </svg>
  );
}

function Tilt(props: StepProps) {
  const s = useLocale().strings.power;
  const { q, pct } = useQ();
  const [city, setCity] = useState<CityId>('cairo');
  const [ti, setTi] = useState(0);
  const [facing, setFacing] = useState<Facing>('E');
  const tilt = at(TILT_STOPS, ti);
  const r = checkTilt(city, tilt, facing);
  return (
    <Shell
      {...props}
      id="tilt" agent={{ briefs: BRIEFS['power:tilt'] ?? [], ctx: { city }, apply: (p) => { if (p.facing) setFacing(p.facing); if (p.tilt !== undefined) setTi(idx(TILT_STOPS, p.tilt as (typeof TILT_STOPS)[number])); } }}
      text={s.steps.tilt}
      version={`${city}:${ti}:${facing}`}
      evaluate={() => ({ ok: r.ok, message: r.ok ? s.tilt.ok(pct(r.share)) : s.tilt.fail(pct(r.share), pct(POINTING_TARGET)) })}
    >
      <div className="fsim">
        <Choice<CityId> label={s.tilt.city} options={CITY_IDS} value={city} onChange={setCity} format={(c) => s.cities[c]} />
        <div className="fsky">
          <SkyFigure city={city} tilt={tilt} facing={facing} />
          <div className="fcompass" role="group" aria-label={s.tilt.facing}>
            {COMPASS.map((f, k) =>
              f ? (
                <button
                  key={f}
                  type="button"
                  className={f === facing ? 'is-on' : ''}
                  aria-pressed={f === facing}
                  onClick={() => {
                    setFacing(f);
                    sound.click();
                  }}
                >
                  {s.compass[f]}
                </button>
              ) : (
                <span key={k} className="fcompass__mid" aria-hidden="true">
                  ☀
                </span>
              ),
            )}
          </div>
        </div>
        <ShareMeter share={r.share} />
        <Stops label={s.tilt.tilt} stops={TILT_STOPS} index={ti} onChange={setTi} format={(t) => q(t, '°')} />
      </div>
    </Shell>
  );
}

/** How much of the best possible light this setup catches, filled in on Check. */
function ShareMeter({ share }: { share: number }) {
  const s = useLocale().strings.power;
  const { pct } = useQ();
  const { tested, p } = useTested();
  return (
    <div className="fbars">
      <div className="fbars__row">
        <span className="fbars__label">{tested ? s.tilt.catches(pct(share * p)) : s.tilt.unknown}</span>
        <span className="fbars__track">
          <span className="fbars__fill" style={{ width: `${(tested ? share * p : 0) * 100}%` }} />
          <span className="fbars__target" style={{ insetInlineStart: `${POINTING_TARGET * 100}%` }} aria-hidden="true" />
        </span>
      </div>
    </div>
  );
}

/* ── 2 and 5. Panels on a roof, month by month ─────────────────────────── */

function Roof({ panels, onChange }: { panels: number; onChange(n: number): void }) {
  const s = useLocale().strings.power;
  const { phase } = useTest();
  return (
    <div className="froof" role="group" aria-label={s.array.roof}>
      {Array.from({ length: ROOF_PANELS }, (_, k) => (
        <button
          key={k}
          type="button"
          className={k < panels ? 'froof__slot is-on' : 'froof__slot'}
          aria-label={s.array.slot(k + 1)}
          aria-pressed={k < panels}
          disabled={phase === 'testing'}
          onClick={() => {
            onChange(k < panels ? k : k + 1);
            sound.click();
          }}
        />
      ))}
    </div>
  );
}

function MonthChart({ made, used }: { made: number[]; used: readonly number[] }) {
  const s = useLocale().strings.power;
  const { tested, p } = useTested();
  const top = Math.max(...made, ...used) * 1.1;
  const h = 90;
  return (
    <svg className="ffig ffig--months" viewBox="0 0 320 120" role="img" aria-hidden="true">
      {used.map((u, m) => {
        const x = 12 + m * 25;
        const grow = tested ? clamp((p - m * 0.04) / 0.5) : 1;
        const mh = ((made[m] ?? 0) / top) * h * grow;
        return (
          <g key={m}>
            <rect className="ffig__made" x={x} y={100 - mh} width={11} height={mh} />
            <rect className="ffig__used" x={x + 11} y={100 - (u / top) * h} width={11} height={(u / top) * h} />
            <text className="ffig__tick" x={x + 11} y={114} textAnchor="middle">
              {s.months[m]}
            </text>
          </g>
        );
      })}
      <line className="ffig__axis" x1={8} x2={312} y1={100} y2={100} />
    </svg>
  );
}

function arrayMessage(r: ReturnType<typeof sizeArray>, s: ReturnType<typeof useLocale>['strings']['power']['array'], pct: (x: number) => string) {
  if (r.ok) return s.ok(pct(r.cover));
  if (r.broken === 'short') return s.short(pct(r.cover), pct(COVER_MIN));
  if (r.broken === 'over') return s.over(pct(r.cover), pct(COVER_MAX));
  return s.roofFull;
}

function ArrayStep(props: StepProps) {
  const s = useLocale().strings.power;
  const { q, pct } = useQ();
  const [panels, setPanels] = useState(3);
  const r = sizeArray('cairo', panels, CAIRO_USE);
  const made = monthlyOutput('cairo', panels);
  return (
    <Shell {...props} id="array" agent={{ briefs: BRIEFS['power:array'] ?? [], apply: (p) => { if (p.panels !== undefined) setPanels(p.panels); } }} text={s.steps.array} version={String(panels)} evaluate={() => ({ ok: r.ok, message: arrayMessage(r, s.array, pct) })}>
      <div className="fsim">
        <Roof panels={panels} onChange={setPanels} />
        <MonthChart made={made} used={CAIRO_USE} />
        <p className="fread">
          <strong>{s.array.made(q(Math.round(r.madeKwh), 'kWh'))}</strong> · {s.array.used(q(r.usedKwh, 'kWh'))} · {s.array.count(panels)}
        </p>
        <p className="fread fread--quiet">{s.array.legend}</p>
      </div>
    </Shell>
  );
}

function PowerAi(props: StepProps) {
  const s = useLocale().strings.power;
  const { q, pct } = useQ();
  const [panels, setPanels] = useState(IRIS_PANELS);
  const [ti, setTi] = useState(TILT_STOPS.indexOf(IRIS_TILT as (typeof TILT_STOPS)[number]));
  const tilt = at(TILT_STOPS, ti);
  const r = sizeArray('berlin', panels, BERLIN_USE, tilt);
  const made = monthlyOutput('berlin', panels, tilt);
  return (
    <Shell
      {...props}
      id="power-ai" agent={{ briefs: BRIEFS['power:power-ai'] ?? [], apply: (p) => { if (p.panels !== undefined) setPanels(p.panels); if (p.tilt !== undefined) setTi(idx(TILT_STOPS, p.tilt as (typeof TILT_STOPS)[number])); } }}
      text={s.steps['power-ai']}
      version={`${panels}:${ti}`}
      evaluate={() => ({ ok: r.ok, message: arrayMessage(r, s.array, pct) })}
    >
      <div className="fsim">
        <IrisSays>{s.iris}</IrisSays>
        <Roof panels={panels} onChange={setPanels} />
        <MonthChart made={made} used={BERLIN_USE} />
        <p className="fread">
          <strong>{s.array.made(q(Math.round(r.madeKwh), 'kWh'))}</strong> · {s.array.used(q(r.usedKwh, 'kWh'))} · {s.array.count(panels)}
        </p>
        <Stops label={s.tilt.tilt} stops={TILT_STOPS} index={ti} onChange={setTi} format={(t) => q(t, '°')} />
      </div>
    </Shell>
  );
}

/* ── 3. Battery ────────────────────────────────────────────────────────── */

function NightFigure({ capacity }: { capacity: number }) {
  const { phase, run } = useTest();
  const tested = phase !== 'idle';
  const p = useLinear(phase === 'testing', run);
  const r = battery(capacity);
  const hour = tested ? p * 12 : 0;
  // The battery's level through the night, hour by hour.
  const levels: number[] = [];
  let left = r.usableKwh;
  for (const h of NIGHT_HOURS) {
    levels.push(left);
    left = Math.max(0, left - h);
  }
  levels.push(left);
  const x = (hr: number) => 20 + hr * 23;
  const y = (kwh: number) => 90 - (kwh / 11.25) * 70;
  const shown = Math.floor(hour);
  const path = levels
    .slice(0, shown + 1)
    .map((l, k) => `${k === 0 ? 'M' : 'L'} ${x(k)} ${y(l)}`)
    .join(' ');
  const dark = tested && hour >= r.lastsHours && r.lastsHours < 12;
  return (
    <svg className={`ffig ffig--night${dark ? ' is-dark' : ''}`} viewBox="0 0 320 130" role="img" aria-hidden="true">
      {NIGHT_HOURS.map((h, k) => (
        <rect key={k} className="ffig__use" x={x(k) + 2} y={100} width={19} height={h * 22} />
      ))}
      <line className="ffig__axis" x1={20} x2={296} y1={90} y2={90} />
      {tested && <path className="ffig__level" d={path} fill="none" />}
      {tested && <line className="ffig__clock" x1={x(hour)} x2={x(hour)} y1={14} y2={124} />}
      <g className="ffig__home" transform="translate(286 18)">
        <path d="M -14 10 L 0 -2 L 14 10 Z" />
        <rect x={-11} y={10} width={22} height={16} />
        <rect className="ffig__window" x={-6} y={14} width={12} height={8} />
      </g>
      {/* Clock times, the same in every language here. */}
      <text className="ffig__tick" x={20} y={12}>
        18:00
      </text>
      <text className="ffig__tick" x={296} y={128} textAnchor="end">
        06:00
      </text>
    </svg>
  );
}

function Battery(props: StepProps) {
  const s = useLocale().strings.power;
  const { q } = useQ();
  const [i, setI] = useState(0);
  const cap = at(BATTERY_STOPS, i);
  const r = battery(cap);
  return (
    <Shell
      {...props}
      id="battery" agent={{ briefs: BRIEFS['power:battery'] ?? [], apply: (p) => { if (p.cap !== undefined) setI(idx(BATTERY_STOPS, p.cap as (typeof BATTERY_STOPS)[number])); } }}
      text={s.steps.battery}
      version={String(i)}
      evaluate={() => ({
        ok: r.ok,
        message: r.ok ? s.battery.ok : r.broken === 'short' ? s.battery.short(q(r.lastsHours, 'h', 1)) : s.battery.budget(q(r.costUsd, 'USD'), q(BATTERY_BUDGET, 'USD')),
      })}
    >
      <div className="fsim">
        <NightFigure capacity={cap} />
        <p className="fread">
          <strong>{s.battery.usable(q(r.usableKwh, 'kWh', 2), q(USABLE * 100, '%'))}</strong> · {s.battery.need(q(NIGHT_KWH, 'kWh'))} ·{' '}
          {s.battery.cost(q(r.costUsd, 'USD'), q(BATTERY_BUDGET, 'USD'))}
        </p>
        <p className="fread fread--quiet">{s.battery.price(q(BATTERY_USD_PER_KWH, 'USD'))}</p>
        <Stops label={s.battery.size} stops={BATTERY_STOPS} index={i} onChange={setI} format={(v) => q(v, 'kWh', 1)} />
      </div>
    </Shell>
  );
}

/* ── 4. The cable ──────────────────────────────────────────────────────── */

function CableFigure({ size }: { size: (typeof SIZE_STOPS)[number] }) {
  const { tested, p, phase } = useTested();
  const { q } = useQ();
  const r = cable(size);
  const w = 1.5 + size * 0.7;
  const volts = MAINS_V - (tested ? r.dropV * p : 0);
  const lamp = tested ? 1 - clamp((r.dropShare / DROP_LIMIT - 0.4) * 0.8) * p : 0.3;
  const verdict = phase === 'pass' ? 'ffig--ok' : phase === 'fail' ? 'ffig--fail' : '';
  return (
    <svg className={`ffig ffig--cable ${verdict}${tested && r.broken === 'hot' ? ' is-hot' : ''}`} viewBox="0 0 320 110" role="img" aria-hidden="true">
      <rect className="ffig__box" x={10} y={40} width={44} height={36} />
      <text className="ffig__tick" x={32} y={62} textAnchor="middle">
        DC→AC
      </text>
      <path className="ffig__cable" d="M 54 58 C 120 90, 200 30, 252 58" strokeWidth={w} fill="none" />
      {tested && phase === 'testing' && <path className="ffig__current" d="M 54 58 C 120 90, 200 30, 252 58" strokeWidth={Math.max(1.5, w / 2)} fill="none" />}
      <g transform="translate(276 50)">
        <path className="ffig__houseline" d="M -24 6 L 0 -16 L 24 6 Z M -18 6 L -18 40 L 18 40 L 18 6" fill="none" />
        <circle className="ffig__lamp" cx={0} cy={18} r={7} opacity={lamp} />
      </g>
      <text className="ffig__label" x={276} y={106} textAnchor="middle">
        {q(volts, 'V', 1)}
      </text>
    </svg>
  );
}

function Cable(props: StepProps) {
  const s = useLocale().strings.power;
  const { q, pct } = useQ();
  const [i, setI] = useState(0);
  const size = at(SIZE_STOPS, i);
  const r = cable(size);
  return (
    <Shell
      {...props}
      id="cable" agent={{ briefs: BRIEFS['power:cable'] ?? [], apply: (p) => { if (p.size !== undefined) setI(idx(SIZE_STOPS, p.size as (typeof SIZE_STOPS)[number])); } }}
      text={s.steps.cable}
      version={String(i)}
      evaluate={() => ({
        ok: r.ok,
        message: r.ok ? s.cable.ok(pct(r.dropShare)) : r.broken === 'hot' ? s.cable.hot(q(r.ratingA, 'A'), q(CABLE_A, 'A')) : s.cable.drop(pct(r.dropShare)),
      })}
    >
      <div className="fsim">
        <p className="fread fread--quiet">
          {q(CABLE_M, 'm')} · {q(CABLE_A, 'A')} · {q(MAINS_V, 'V')}
        </p>
        <CableFigure size={size} />
        <p className="fread">
          <strong>{s.cable.dropRead(pct(r.dropShare))}</strong> · {s.cable.rating(q(r.ratingA, 'A'))}
        </p>
        <Stops label={s.cable.size} stops={SIZE_STOPS} index={i} onChange={setI} format={(v) => q(v, 'mm²', v % 1 ? 1 : 0)} />
      </div>
    </Shell>
  );
}

export const POWER_STEPS: Record<PowerStepId, ComponentType<StepProps>> = {
  tilt: Tilt,
  array: ArrayStep,
  battery: Battery,
  cable: Cable,
  'power-ai': PowerAi,
};
